import React, { useEffect, useState } from 'react';
import { Modal, Form } from 'antd';
import { ButtonCommon } from 'components/common';
import { AddCategoryModalProps } from 'types';
import { useAddCategoryOperations } from 'hooks/useAddCategoryOperations';
import { CategoryFormSections } from '../Section/CategoryFormSections';

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  isOpen,
  onCancel,
  onGenerate,
  onSuccess,
  form: externalForm,
  isCopyMode = false,
  editMode = false,
  editingCategory = null,
}) => {
  const [rowValues, setRowValues] = useState<Record<number, string[]>>({
    0: ['', '', '', ''],
  });
  const [confirmedRows, setConfirmedRows] = useState<number[]>([]);
  const [localForm] = Form.useForm();
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
  const activeForm = externalForm || localForm;
  const [codePrefix, setCodePrefix] = useState<string>('');
  const {
    selectedValues,
    isAttributesLoading,
    isActive,
    genCode,
    categoryDetail,
    attributeOptions,
    shouldLoadAttributes,
    setShouldLoadAttributes,
    setSelectedValues,
    handleSubmit,
    handleAttributeSearch,
    handleToggleActive,
    isLoadingCategoryDetail,
    isSubmitting,
    // categoryDetailError,
  } = useAddCategoryOperations({
    editMode,
    editingCategory,
    onGenerate,
    onSuccess,
    isOpen,
    isCopyMode,
    setExternalRowValues: setRowValues,
    setExternalConfirmedRows: setConfirmedRows,
    formInstance: activeForm,
  });

  useEffect(() => {
    if (genCode && (isCopyMode || !editMode)) {
      const prefix = genCode.substring(0, 6);
      setCodePrefix(prefix);
    } else if (categoryDetail?.code && editMode) {
      const prefix = categoryDetail.code.substring(0, 6);
      setCodePrefix(prefix);
    }
  }, [categoryDetail?.code, genCode]);

  const handleInputChange = (rowIndex: number, colIndex: number, value: string) => {
    setRowValues((prev) => ({
      ...prev,
      [rowIndex]: prev[rowIndex].map((v, i) => (i === colIndex ? value : v)),
    }));
  };

  const confirmRow = (rowIndex: number) => {
    const values = rowValues[rowIndex] || ['', '', '', ''];
    const hasValue = values.some((v) => v && v.trim() !== '');

    if (hasValue) {
      setConfirmedRows((prev) => [...prev, rowIndex]);
      return true;
    }
    return false;
  };

  const unconfirmRow = (rowIndex: number) => {
    setConfirmedRows((prev) => prev.filter((idx) => idx !== rowIndex));
  };

  const addNewRow = () => {
    const newRowIndex = Object.keys(rowValues).length;
    setRowValues((prev) => ({
      ...prev,
      [newRowIndex]: ['', '', '', ''],
    }));
  };

  const removeRow = (rowIndex: number) => {
    const newRowValues = { ...rowValues };
    delete newRowValues[rowIndex];

    const updatedRowValues: Record<number, string[]> = {};
    Object.values(newRowValues).forEach((value, index) => {
      updatedRowValues[index] = value;
    });

    setRowValues(updatedRowValues);
    setConfirmedRows((prev) => prev.filter((idx) => idx !== rowIndex));
  };

  useEffect(() => {
    if (isOpen) {
      setIsFormLoading(true);

      if (!editMode) {
        activeForm.resetFields();
        setRowValues({ 0: ['', '', '', ''] });
        setConfirmedRows([]);
        setIsFormLoading(false);
      }
    } else {
      setIsFormLoading(false);
    }
  }, [isOpen, editMode, activeForm]);

  useEffect(() => {
    if (isOpen && editMode) {
      if (categoryDetail) {
        setTimeout(() => {
          setIsFormLoading(false);
        }, 500); // time to set up form values
      } else {
        setIsFormLoading(true);
      }
    }
  }, [isOpen, editMode, isLoadingCategoryDetail, categoryDetail]);

  useEffect(() => {
    if (isOpen || editingCategory?.id) {
      activeForm.resetFields();
      if (!editMode) {
        setRowValues({ 0: ['', '', '', ''] });
        setConfirmedRows([]);
      } else {
        setRowValues({});
        setConfirmedRows([]);
      }
    }
  }, [isOpen, editingCategory?.id, activeForm, editMode]);

  return (
    <Modal
      open={isOpen}
      maskClosable={false}
      // onCancel={onCancel}
      footer={null}
      width={1400}
      centered
      className='add-category-modal'
      closeIcon={null}
      loading={isLoadingCategoryDetail || isFormLoading}
      destroyOnClose
      title={
        <div className='flex flex-col bg-white py-[12px] px-[24px] rounded-lg gap-2'>
          <h2 className='text-[20px] font-bold leading-[24px]'>
            {editMode && !isCopyMode ? 'CẬP NHẬT DANH MỤC' : isCopyMode ? 'SAO CHÉP DANH MỤC' : 'TẠO DANH MỤC MỚI'}
          </h2>
          <p className='text-[14px] leading-[20px] text-[var(--textBlack56)] font-normal'>
            {isCopyMode
              ? 'Vui lòng chỉnh sửa thông tin (nếu cần) trước khi sao chép danh mục'
              : editMode
                ? 'Vui lòng điền đầy đủ thông tin để cập nhật danh mục'
                : 'Vui lòng điền đầy đủ thông tin trước khi thêm danh mục mới'}
          </p>
        </div>
      }
    >
      <>
        <div className='flex gap-1 px-[24px] py-2 bg-[var(--bgGrey)] '>
          <div className='flex-1 flex flex-col gap-1 p-4 max-h-[calc(100vh-180px)] overflow-y-auto bg-white rounded-lg '>
            <h3 className='text-[20px] leading-[24px] font-bold'>Thông tin danh mục</h3>

            <CategoryFormSections
              activeForm={activeForm}
              codePrefix={codePrefix}
              setCodePrefix={setCodePrefix}
              onGenerate={onGenerate}
              editMode={editMode}
              isCopyMode={isCopyMode}
              rowValues={rowValues}
              confirmedRows={confirmedRows}
              handleInputChange={handleInputChange}
              confirmRow={confirmRow}
              unconfirmRow={unconfirmRow}
              removeRow={removeRow}
              addNewRow={addNewRow}
              selectedValues={selectedValues}
              isAttributesLoading={isAttributesLoading}
              isActive={isActive}
              attributeOptions={attributeOptions}
              shouldLoadAttributes={shouldLoadAttributes}
              setShouldLoadAttributes={setShouldLoadAttributes}
              setSelectedValues={setSelectedValues}
              handleAttributeSearch={handleAttributeSearch}
              handleToggleActive={handleToggleActive}
              handleSubmit={handleSubmit}
            />
          </div>
        </div>

        <div className='flex justify-center mt-4 gap-3'>
          <ButtonCommon color='none' size='md' radius='rounded' onClick={onCancel}>
            Hủy
          </ButtonCommon>
          <ButtonCommon
            color='primary'
            size='md'
            radius='rounded'
            loading={isSubmitting}
            onClick={() => {
              const saleDimensionsData = {
                rowValues,
                confirmedRows,
              };
              const serializedData = JSON.stringify(saleDimensionsData);
              activeForm.setFieldsValue({
                saleDimensionsData: serializedData,
              });
              activeForm.submit();
            }}
          >
            {editMode && !isCopyMode ? 'Cập nhật danh mục' : isCopyMode ? 'Sao chép danh mục' : 'Thêm danh mục'}
          </ButtonCommon>
        </div>
      </>
    </Modal>
  );
};

export default AddCategoryModal;
